import { Injectable, Injector } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { SwPush } from '@angular/service-worker';
import { Observable, Subscription } from 'rxjs';
import { environment } from 'src/environments/environment';
import { EndpointFactory } from './endpoint.service';

export interface NotificationPayload {
  title: string;
  body: string;
  icon?: string;
  badge?: string;
  data?: any;
}

export interface Keys {
  p256dh: string;
  auth: string;
}

export interface PushSubscriptionDto {
  endpoint: string;
  keys: Keys;
  userId?: string;
  userAgent?: string;
  timestamp?: string;
  preferences?: any;
}

@Injectable({
  providedIn: 'root'
})
export class PushNotificationService {
  private readonly VAPID_PUBLIC_KEY = environment.webPush?.publicKey || 'BM1cL1p8Jtm7kVakZuQ-Dv4A2eA-GXVtLikLcxfn3iULxTPTLkqgLKJoJKdgQIz4kv8DskCVQeDLkUj0S_tpANM';

  // Add subscription management
  private subscriptions = new Subscription();
  private isInitialized = false;

  // Lazy-loaded AuthService to break circular dependency
  private _authService: any;

  constructor(
    private swPush: SwPush,
    private http: HttpClient,
    private endpoints: EndpointFactory,
    private injector: Injector
  ) {}

  /**
   * Lazy getter for AuthService to avoid circular dependency
   */
  private get authService(): any {
    if (!this._authService) {
      // Dynamically get AuthService when needed
      this._authService = this.injector.get('AuthService' as any);
    }
    return this._authService;
  }

  /**
   * Check if push notifications are supported and service worker is enabled
   */
  get isEnabled(): boolean {
    return this.swPush.isEnabled;
  }

  /**
   * Request permission and subscribe to push notifications
   */
  requestSubscription(): Promise<PushSubscription | null> {
    if (!this.isEnabled) {
      console.warn('Push notifications are not enabled');
      return Promise.resolve(null);
    }

    return this.swPush.requestSubscription({
      serverPublicKey: this.VAPID_PUBLIC_KEY
    }).catch(error => {
      console.error('Could not subscribe to notifications', error);
      return null;
    });
  }

  /**
   * Get the current push subscription
   */
  get subscription(): Observable<PushSubscription | null> {
    return this.swPush.subscription;
  }

  /**
   * Listen for push notification messages
   */
  get messages(): Observable<any> {
    return this.swPush.messages;
  }

  /**
   * Listen for notification clicks
   */
  get notificationClicks(): Observable<any> {
    return this.swPush.notificationClicks;
  }

  /**
   * Unsubscribe from push notifications
   */
  unsubscribe(): Promise<boolean> {
    if (!this.isEnabled) {
      return Promise.resolve(false);
    }

    return this.swPush.unsubscribe().then(() => true).catch(error => {
      console.error('Could not unsubscribe from notifications', error);
      return false;
    });
  }

  /**
   * Check if the user has granted notification permission
   */
  checkPermission(): NotificationPermission {
    if ('Notification' in window) {
      return Notification.permission;
    }
    return 'denied';
  }

  /**
   * Request notification permission
   */
  async requestPermission(): Promise<NotificationPermission> {
    if ('Notification' in window) {
      const permission = await Notification.requestPermission();
      return permission;
    }
    return 'denied';
  }

  /**
   * Show a local notification (fallback for when push is not available)
   */
  showLocalNotification(payload: NotificationPayload): void {
    if ('Notification' in window && Notification.permission === 'granted') {
      const notification = new Notification(payload.title, {
        body: payload.body,
        icon: payload.icon || '/assets/img/logo_letters/u.png',
        badge: payload.badge || '/assets/img/logo_letters/u.png',
        data: payload.data
      });

      notification.onclick = (event) => {
        event.preventDefault();
        window.focus();
        notification.close();
      };
    }
  }

  /**
   * Initialize push notifications
   */
  async initializePushNotifications(): Promise<void> {
    // Prevent multiple initializations
    if (this.isInitialized) {
      console.log('Push notifications already initialized');
      return;
    }

    // Skip initialization in development if service worker is not available
    if (!this.isEnabled) {
      console.warn('Push notifications are not enabled (service worker not available)');
      return;
    }

    try {
      // Check permission
      const permission = this.checkPermission();

      if (permission === 'denied') {
        console.warn('Push notifications are denied');
        return;
      }

      if (permission === 'default') {
        const newPermission = await this.requestPermission();
        if (newPermission !== 'granted') {
          console.warn('Push notification permission was not granted');
          return;
        }
      }

      // Subscribe to push notifications
      const subscription = await this.requestSubscription();

      if (subscription) {
        console.log('Successfully subscribed to push notifications:', subscription);

        // Send subscription to your server here
        // await this.sendSubscriptionToServer(subscription);
      }

      // Listen for messages - properly manage subscription
      const messagesSub = this.messages.subscribe({
        next: (message) => {
          console.log('Received push message:', message);
          // Handle the received message
          this.handlePushMessage(message);
        },
        error: (error) => {
          console.error('Error in push messages subscription:', error);
        }
      });
      this.subscriptions.add(messagesSub);

      // Listen for notification clicks - properly manage subscription
      const clicksSub = this.notificationClicks.subscribe({
        next: (click) => {
          console.log('Notification clicked:', click);
          // Handle notification click
          this.handleNotificationClick(click);
        },
        error: (error) => {
          console.error('Error in notification clicks subscription:', error);
        }
      });
      this.subscriptions.add(clicksSub);

      this.isInitialized = true;
      console.log('Push notifications initialized successfully');

    } catch (error) {
      console.error('Error initializing push notifications:', error);
      // Reset initialization state on error
      this.isInitialized = false;
    }
  }

  /**
   * Cleanup subscriptions and reset initialization state
   */
  cleanup(): void {
    this.subscriptions.unsubscribe();
    this.subscriptions = new Subscription();
    this.isInitialized = false;
    console.log('Push notification subscriptions cleaned up');
  }

  /**
   * Handle incoming push messages
   */
  private handlePushMessage(message: any): void {
    const payload: NotificationPayload = {
      title: message.notification?.title || 'New Message',
      body: message.notification?.body || 'You have a new notification',
      icon: message.notification?.icon,
      badge: message.notification?.badge,
      data: message.data
    };

    this.showLocalNotification(payload);
  }

  /**
   * Handle notification clicks
   */
  private handleNotificationClick(click: any): void {
    // Navigate to specific route or perform action based on notification data
    if (click.action) {
      console.log('Notification action clicked:', click.action);
    }

    if (click.notification?.data?.url) {
      window.open(click.notification.data.url, '_blank');
    }
  }

  /**
   * Save notification preferences to localStorage
   */
  saveNotificationPreferences(enabled: boolean, autoSubscribe: boolean = true): void {
    const preferences = {
      enabled,
      autoSubscribe,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem('notificationPreferences', JSON.stringify(preferences));
  }

  /**
   * Get notification preferences from localStorage
   */
  getNotificationPreferences(): { enabled: boolean; autoSubscribe: boolean; timestamp?: string } {
    const stored = localStorage.getItem('notificationPreferences');
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch (error) {
        console.error('Error parsing notification preferences:', error);
      }
    }
    return { enabled: true, autoSubscribe: true }; // Default to enabled
  }

  /**
   * Check if user wants auto-subscription
   */
  shouldAutoSubscribe(): boolean {
    const preferences = this.getNotificationPreferences();
    return preferences.enabled && preferences.autoSubscribe;
  }

  /**
   * Get subscription status with more details
   */
  async getSubscriptionStatus(): Promise<{
    isSupported: boolean;
    permission: NotificationPermission;
    isSubscribed: boolean;
    subscription: PushSubscription | null;
    preferences: any;
  }> {
    return new Promise((resolve) => {
      this.subscription.subscribe(subscription => {
        resolve({
          isSupported: this.isEnabled,
          permission: this.checkPermission(),
          isSubscribed: !!subscription,
          subscription: subscription,
          preferences: this.getNotificationPreferences()
        });
      });
    });
  }

  /**
   * Send subscription to server (enhanced version)
   */
  async sendSubscriptionToServer(subscription: PushSubscription, userId?: string): Promise<boolean> {
    try {
      const p256dhKey = subscription.getKey ? subscription.getKey('p256dh') : null;
      const authKey = subscription.getKey ? subscription.getKey('auth') : null;

      console.log('p256dhKey', p256dhKey);
      console.log('authKey', authKey);
      console.log('subscription.endpoint', subscription.endpoint);

      // Ensure we have valid keys before proceeding
      if (!p256dhKey || !authKey) {
        console.error('Missing required subscription keys');
        return false;
      }

      const subscriptionData: PushSubscriptionDto = {
        endpoint: subscription.endpoint,
        keys: {
          p256dh: btoa(String.fromCharCode(...new Uint8Array(p256dhKey))),
          auth: btoa(String.fromCharCode(...new Uint8Array(authKey)))
        },
        userId: userId,
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString(),
        preferences: this.getNotificationPreferences()
      };

      console.log('Sending subscription to server:', subscriptionData);

      // Get the Bearer token from auth service
      const token = this.authService.getAuthBearerToken();

      // Create headers with Bearer token
      const headers = new HttpHeaders({
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      });

      // Send to your server API using the correct endpoint with Bearer token
      const response = await this.http.post(this.endpoints.PushNotificationSubscribe, subscriptionData, { headers }).toPromise();

      console.log('Subscription sent to server successfully:', response);
      return true;

    } catch (error: any) {
      console.error('Failed to send subscription to server:', error);

      // If it's a network error or server not available, still return true for development
      if (error.status === 0 || error.status === 404 || error.name === 'HttpErrorResponse') {
        console.warn('Server endpoint not available - subscription logged locally for development');
        return true; // Don't fail the subscription process due to server issues
      }

      return false;
    }
  }

  /**
   * Update subscription on server (for subscription changes)
   */
  async updateSubscriptionOnServer(oldSubscription: PushSubscription | null, newSubscription: PushSubscription | null, userId?: string): Promise<boolean> {
    try {
      const updateData = {
        oldSubscription: oldSubscription ? {
          endpoint: oldSubscription.endpoint
        } : null,
        newSubscription: newSubscription ? await this.prepareSubscriptionData(newSubscription, userId) : null,
        userId: userId,
        timestamp: new Date().toISOString()
      };

      console.log('Updating subscription on server:', updateData);

      const response = await this.http.put(this.endpoints.PushNotificationUpdate, updateData).toPromise();

      console.log('Subscription updated on server successfully:', response);
      return true;

    } catch (error: any) {
      console.error('Failed to update subscription on server:', error);

      // Handle gracefully in development
      if (error.status === 0 || error.status === 404 || error.name === 'HttpErrorResponse') {
        console.warn('Server endpoint not available - subscription update logged locally for development');
        return true;
      }

      return false;
    }
  }

  /**
   * Remove subscription from server
   */
  async removeSubscriptionFromServer(subscription: PushSubscription, userId?: string): Promise<boolean> {
    try {
      const removeData = {
        endpoint: subscription.endpoint,
        userId: userId,
        timestamp: new Date().toISOString()
      };

      console.log('Removing subscription from server:', removeData);

      const response = await this.http.delete(this.endpoints.PushNotificationUnsubscribe, {
        body: removeData
      }).toPromise();

      console.log('Subscription removed from server successfully:', response);
      return true;

    } catch (error: any) {
      console.error('Failed to remove subscription from server:', error);

      // Handle gracefully in development
      if (error.status === 0 || error.status === 404 || error.name === 'HttpErrorResponse') {
        console.warn('Server endpoint not available - subscription removal logged locally for development');
        return true;
      }

      return false;
    }
  }

  /**
   * Prepare subscription data for server transmission
   */
  private async prepareSubscriptionData(subscription: PushSubscription, userId?: string): Promise<PushSubscriptionDto> {
    const p256dhKey = subscription.getKey ? subscription.getKey('p256dh') : null;
    const authKey = subscription.getKey ? subscription.getKey('auth') : null;

    // Ensure we have valid keys
    if (!p256dhKey || !authKey) {
      throw new Error('Missing required subscription keys');
    }

    return {
      endpoint: subscription.endpoint,
      keys: {
        p256dh: btoa(String.fromCharCode(...new Uint8Array(p256dhKey))),
        auth: btoa(String.fromCharCode(...new Uint8Array(authKey)))
      },
      userId: userId,
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString(),
      preferences: this.getNotificationPreferences()
    };
  }
}
